import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import { Controller, useForm } from "react-hook-form";

import { WikiSchemas } from "@/api/router/wiki/schemas";
import { UnsavedGuard } from "@/components/shared/form/unsaved-guard";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useWikiCreate } from "@/hooks/use-wiki";
import { errorCode } from "@/lib/error-code";
import { WikiLabels } from "@/lib/labels/wiki";

import { WikiEditor } from "./wiki-editor";

export function WikiCreate({ onClose }: { onClose: () => void }) {
	const navigate = useNavigate();
	const [confirmingClose, setConfirmingClose] = useState(false);

	const { mutate, isPending, error } = useWikiCreate();
	const {
		control,
		register,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<typeof WikiSchemas.create.inferIn>({
		resolver: arktypeResolver(WikiSchemas.create, undefined, { raw: true }),
		mode: "onChange",
		defaultValues: { title: "", content: "" },
	});

	const submit = handleSubmit((values) => {
		mutate(values, {
			onSuccess: (page) => {
				onClose();
				void navigate({ to: "/wiki/$slug", params: { slug: page.slug } });
			},
		});
	});

	function requestClose() {
		if (isDirty && !isPending) {
			setConfirmingClose(true);

			return;
		}

		onClose();
	}

	const errorMessage =
		errorCode(error) === "CONFLICT" ? WikiLabels.conflict : WikiLabels.createError;

	return (
		<Modal
			title={WikiLabels.newPage}
			onClose={requestClose}
			className="max-w-2xl"
			slot="wiki-create-modal"
			isPending={isPending}
		>
			<form onSubmit={submit} className="flex flex-col gap-4">
				<Input
					type="text"
					{...register("title")}
					autoFocus
					disabled={isPending}
					placeholder={WikiLabels.titlePlaceholder}
					className="font-display font-semibold"
				/>

				<Controller
					control={control}
					name="content"
					render={({ field }) => (
						<WikiEditor
							value={field.value}
							onChange={field.onChange}
							disabled={isPending}
							onKeyDown={(event) => {
								if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
									void submit();
								}
							}}
						/>
					)}
				/>

				{!!error && <p className="text-xs text-destructive">{errorMessage}</p>}

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button
						size="sm"
						variant="secondary"
						type="button"
						disabled={isPending}
						onClick={requestClose}
					>
						{WikiLabels.cancel}
					</Button>

					<Button
						size="sm"
						type="submit"
						variant="default"
						className="px-4"
						disabled={!isValid || isPending}
					>
						{isPending && <Loader2 className="animate-spin" />}
						{!isPending && <Plus />}
						{WikiLabels.create}
					</Button>
				</div>
			</form>

			{confirmingClose && (
				<UnsavedGuard onStay={() => setConfirmingClose(false)} onDiscard={onClose} />
			)}
		</Modal>
	);
}
